import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  MapPin, Star, Clock, Users, Shield, BadgeCheck, Heart,
  ChevronRight, MessageSquare, Share2, Eye, Calendar, Phone,
  Wifi, Droplets, Car, Camera, Tv, Zap
} from 'lucide-react';
import api from '../utils/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const AMENITY_ICONS = {
  wifi: Wifi, showers: Droplets, parking: Car, cameras: Camera, screens: Tv, lighting: Zap
};

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);

  useEffect(() => {
    fetchListing();
  }, [id]);

  const fetchListing = async () => {
    try {
      const res = await api.get(`/listings/${id}`);
      setListing(res.data.data);
      setIsFavorited(res.data.data.is_favorited || false);
    } catch (err) {
      toast.error('لم يتم العثور على الإعلان');
      navigate('/listings');
    } finally {
      setLoading(false);
    }
  };

  const handleFavorite = async () => {
    if (!user) return navigate('/login');
    try {
      const res = await api.post(`/favorites/${listing.id}`);
      setIsFavorited(res.data.favorited);
      toast.success(res.data.favorited ? 'تمت إضافة إلى المفضلة' : 'تمت الإزالة من المفضلة');
    } catch {}
  };

  const handleShare = () => {
    navigator.share?.({ title: listing.title, url: window.location.href }) ||
      navigator.clipboard.writeText(window.location.href).then(() => toast.success('تم نسخ الرابط'));
  };

  if (loading) return (
    <div className="min-h-screen pt-20 max-w-7xl mx-auto px-4 py-8">
      <div className="skeleton h-96 rounded-2xl mb-6" />
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-4">
          <div className="skeleton h-8 rounded-xl" />
          <div className="skeleton h-4 rounded-xl w-2/3" />
          <div className="skeleton h-32 rounded-xl" />
        </div>
        <div className="skeleton h-64 rounded-2xl" />
      </div>
    </div>
  );

  if (!listing) return null;

  const allImages = listing.images?.length > 0
    ? listing.images.map(img => `${process.env.REACT_APP_API_URL?.replace('/api', '')}${img.image_url}`)
    : ['https://images.unsplash.com/photo-1459865264687-595d652de67e?w=800&h=500&fit=crop'];

  const amenities = listing.amenities ? (typeof listing.amenities === 'string' ? JSON.parse(listing.amenities) : listing.amenities) : [];

  return (
    <div className="min-h-screen pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-dark-400 mb-6">
          <Link to="/" className="hover:text-white transition-colors">الرئيسية</Link>
          <ChevronRight size={14} />
          <Link to="/listings" className="hover:text-white transition-colors">الإعلانات</Link>
          <ChevronRight size={14} />
          <span className="text-white">{listing.title}</span>
        </div>

        {/* Gallery */}
        <div className="grid grid-cols-4 gap-2 mb-6 rounded-2xl overflow-hidden h-80 md:h-96">
          <div className="col-span-3">
            <img src={allImages[activeImage]} alt={listing.title}
              className="w-full h-full object-cover cursor-pointer" />
          </div>
          <div className="grid grid-rows-3 gap-2">
            {allImages.slice(0, 3).map((img, i) => (
              <img key={i} src={img} alt="" onClick={() => setActiveImage(i)}
                className={`w-full h-full object-cover cursor-pointer transition-opacity ${activeImage === i ? 'opacity-100 ring-2 ring-primary-500' : 'opacity-70 hover:opacity-100'}`} />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title & Actions */}
            <div>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="badge bg-primary-500/20 text-primary-400 text-xs">{listing.category_name_ar}</span>
                    {listing.is_verified && <span className="badge bg-blue-500/20 text-blue-400 text-xs flex items-center gap-1"><BadgeCheck size={10} /> موثق</span>}
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{listing.title}</h1>
                  <div className="flex items-center gap-4 text-sm text-dark-400">
                    <span className="flex items-center gap-1"><MapPin size={14} className="text-primary-400" />{listing.city}{listing.area ? `، ${listing.area}` : ''}</span>
                    <span className="flex items-center gap-1"><Eye size={14} />{listing.views_count} مشاهدة</span>
                    {listing.rating_count > 0 && (
                      <span className="flex items-center gap-1">
                        <Star size={14} className="text-accent fill-accent" />
                        {parseFloat(listing.rating_avg).toFixed(1)} ({listing.rating_count} تقييم)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={handleFavorite}
                    className={`p-2.5 rounded-xl border transition-colors ${isFavorited ? 'bg-red-500/10 border-red-500/50 text-red-400' : 'bg-dark-700 border-dark-600 text-dark-400 hover:text-red-400'}`}>
                    <Heart size={18} fill={isFavorited ? 'currentColor' : 'none'} />
                  </button>
                  <button onClick={handleShare}
                    className="p-2.5 rounded-xl border bg-dark-700 border-dark-600 text-dark-400 hover:text-white transition-colors">
                    <Share2 size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5">
              <h2 className="text-white font-bold text-lg mb-3">وصف الإعلان</h2>
              <p className="text-dark-300 leading-relaxed text-sm">{listing.description || 'لا يوجد وصف متاح'}</p>
            </div>

            {/* Details */}
            <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5">
              <h2 className="text-white font-bold text-lg mb-4">تفاصيل المنشأة</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {listing.capacity && (
                  <div className="text-center p-3 bg-dark-700 rounded-xl">
                    <Users size={20} className="text-primary-400 mx-auto mb-1" />
                    <div className="text-white font-semibold text-sm">{listing.capacity}</div>
                    <div className="text-dark-400 text-xs">الطاقة الاستيعابية</div>
                  </div>
                )}
                {listing.size_sqm && (
                  <div className="text-center p-3 bg-dark-700 rounded-xl">
                    <div className="text-primary-400 text-xl font-bold mx-auto mb-1">م²</div>
                    <div className="text-white font-semibold text-sm">{listing.size_sqm}</div>
                    <div className="text-dark-400 text-xs">المساحة</div>
                  </div>
                )}
                <div className="text-center p-3 bg-dark-700 rounded-xl">
                  <Clock size={20} className="text-primary-400 mx-auto mb-1" />
                  <div className="text-white font-semibold text-sm">{listing.min_booking_hours}-{listing.max_booking_hours}</div>
                  <div className="text-dark-400 text-xs">ساعة (نطاق الحجز)</div>
                </div>
              </div>
            </div>

            {/* Amenities */}
            {amenities.length > 0 && (
              <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5">
                <h2 className="text-white font-bold text-lg mb-4">المرافق والميزات</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {amenities.map((amenity, i) => {
                    const Icon = AMENITY_ICONS[amenity] || Shield;
                    return (
                      <div key={i} className="flex items-center gap-2 text-dark-200 text-sm">
                        <Icon size={14} className="text-primary-400" /> {amenity}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Rules */}
            {listing.rules && (
              <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5">
                <h2 className="text-white font-bold text-lg mb-3">القواعد والشروط</h2>
                <p className="text-dark-300 text-sm leading-relaxed">{listing.rules}</p>
              </div>
            )}

            {/* Reviews */}
            <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5">
              <h2 className="text-white font-bold text-lg mb-4">
                التقييمات ({listing.rating_count || 0})
              </h2>
              {listing.reviews?.length > 0 ? (
                <div className="space-y-4">
                  {listing.reviews.map(review => (
                    <div key={review.id} className="border-b border-dark-700 pb-4 last:border-0">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-full bg-dark-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                          {review.reviewer_name?.charAt(0)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-white text-sm font-medium">{review.reviewer_name}</span>
                            <div className="flex items-center gap-0.5">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} size={12} className={i < review.rating ? 'text-accent fill-accent' : 'text-dark-600'} />
                              ))}
                            </div>
                          </div>
                          <p className="text-dark-300 text-sm">{review.comment}</p>
                          <span className="text-dark-500 text-xs mt-1 block">
                            {format(new Date(review.created_at), 'dd/MM/yyyy')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-dark-400 text-sm text-center py-4">لا توجد تقييمات بعد</p>
              )}
            </div>
          </div>

          {/* Booking Widget */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5">
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-primary-400">{listing.price_per_hour}</span>
                    <span className="text-dark-400 text-sm">درهم / ساعة</span>
                  </div>
                  {listing.price_per_day && (
                    <p className="text-dark-400 text-xs mt-1">أو {listing.price_per_day} درهم / يوم</p>
                  )}
                </div>

                {listing.rating_count > 0 && (
                  <div className="flex items-center gap-1 mb-4 pb-4 border-b border-dark-700">
                    <Star size={14} className="text-accent fill-accent" />
                    <span className="text-white text-sm font-semibold">{parseFloat(listing.rating_avg).toFixed(1)}</span>
                    <span className="text-dark-400 text-xs">({listing.rating_count} تقييم)</span>
                  </div>
                )}

                {user ? (
                  user.id === listing.owner_id ? (
                    <div className="text-center py-3 text-dark-400 text-sm">هذا إعلانك الخاص</div>
                  ) : (
                    <Link to={`/booking/${listing.uuid}`}
                      className="btn-primary w-full justify-center text-center py-3 text-base">
                      <Calendar size={18} />
                      احجز الآن
                    </Link>
                  )
                ) : (
                  <Link to="/login" className="btn-primary w-full justify-center text-center py-3 text-base">
                    سجل دخول للحجز
                  </Link>
                )}

                {/* Contact Owner */}
                {user && user.id !== listing.owner_id && (
                  <Link to={`/dashboard/messages?to=${listing.owner_id}&listing=${listing.id}`}
                    className="btn-secondary w-full justify-center mt-3">
                    <MessageSquare size={16} />
                    تواصل مع المالك
                  </Link>
                )}

                {/* Owner Info */}
                <div className="mt-5 pt-4 border-t border-dark-700">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold">
                      {listing.owner_name?.charAt(0)}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-white text-sm font-medium">{listing.owner_name}</span>
                        {listing.owner_verified && <BadgeCheck size={13} className="text-primary-400" />}
                      </div>
                      <span className="text-dark-400 text-xs">مالك المنشأة</span>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-xs text-dark-400">
                    <Shield size={12} className="text-primary-400" />
                    <span>دفعتك محمية بضمان SPORTIX</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
